import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './CharacterEditor.css'
import { useCharacter } from "./CharacterProvider";
import CharacterSelector from "./CharacterSelector";
import CharacterForm from "./CharacterForm";
import CharacterSkills from "./CharacterSkills";
import CharacterAssignmentPopover from "./CharacterAssignmentPopover";
import { printCharacterSheet, previewCharacterSheet } from "./printCharacterSheet";
import * as api from "../../utils/api";


const CharacterEditor = ({token, playerId, staffPermissions}) => {
    const {currentCharacter, setCurrentCharacter} = useCharacter();
    const [races, setRaces] = useState([]);
    const [skillsets, setSkillsets] = useState([]);
    const [availableSkills, setAvailableSkills] = useState([]);
    const [saving, setSaving] = useState(false);
    const [autosave, setAutosave] = useState(
        () => localStorage.getItem('autosave') !== 'false'
    );

    const prevCharacterIdRef = useRef(null);
    const autosaveTimerRef = useRef(null);

    useEffect(() => {
        document.title = currentCharacter?.name
            ? `${currentCharacter.name} | Chronicles Character Manager`
            : 'Character Manager';
        return () => { document.title = 'Chronicles'; };
    }, [currentCharacter?.name]);


    const saveCharacter = useCallback(async (characterData, { silent = false } = {}) => {
        setSaving(true);
        try {
            const payload = { ...characterData, race: characterData.raceId };
            await api.updateCharacter(characterData.characterId, payload);
            if (!silent) toast.success("Save successful!");
        } catch {
            toast.error("Failed to save character");
        } finally {
            setTimeout(() => setSaving(false), 500);
        }
    }, []);

    // Re-fetches the currently open character so its characterStatus and
    // status-scaled SP fields reflect a just-made Primary/Secondary change,
    // without dropping client-only fields (transformName/playerName) that
    // aren't part of the API response.
    const refreshCurrentCharacter = useCallback(async () => {
        if (!currentCharacter?.characterId) return;
        try {
            const refreshed = await api.fetchCharacter(currentCharacter.characterId);
            setCurrentCharacter((prev) => (prev ? { ...prev, ...refreshed } : prev));
        } catch {
            toast.error('Failed to refresh character after assignment change.');
        }
    }, [currentCharacter?.characterId, setCurrentCharacter]);

    // Autosave: fires 1 s after any change to currentCharacter, but not on initial character load/switch.
    useEffect(() => {
        if (!currentCharacter?.characterId) return;

        // Character was switched — record the new ID but don't save.
        if (prevCharacterIdRef.current !== currentCharacter.characterId) {
            prevCharacterIdRef.current = currentCharacter.characterId;
            return;
        }

        if (!autosave) return;

        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            saveCharacter(currentCharacter, { silent: true });
        }, 1000);

        return () => clearTimeout(autosaveTimerRef.current);
    }, [currentCharacter, autosave, saveCharacter]);

    useEffect(() => {
        (async function () {
            try {
                setRaces(await api.fetchRaces());
                setSkillsets(await api.fetchSkillsets());
            } catch {
                toast.error("Failed to load races/skillsets");
            }
        })();
    }, []);

    useEffect(() => {
        async function fetchSkillsForCharacter() {
            if (currentCharacter?.characterId) {
                try {
                    const fetchedSkills = await api.fetchSkills({ characterId: currentCharacter.characterId });
                    setAvailableSkills(fetchedSkills);
                } catch {
                    toast.error("Failed to load available skills");
                }
            }
        }
        fetchSkillsForCharacter();
        // eslint-disable-next-line
    }, [currentCharacter?.characterId, token]);

    const handlePrintCharacterSheet = useCallback(() => {
        if (!currentCharacter) return;
        printCharacterSheet({
            character: currentCharacter,
            races,
            skillsets,
            availableSkills,
            printedByPlayerId: playerId,
        });
    }, [currentCharacter, races, skillsets, availableSkills, playerId]);

    // Ctrl+P (Cmd+P on Mac) triggers the same character sheet print as the
    // "Print Character Sheet" button, instead of the browser's own print
    // dialog for the Character Manager page itself.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
                e.preventDefault();
                handlePrintCharacterSheet();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [handlePrintCharacterSheet]);


    return (
        <div className="character-select-container">
            <h1 className="landing-title">Character Manager</h1>
            <div className="selector-print-row">
                <CharacterSelector token={token} playerId={playerId} staffPermissions={staffPermissions} />
                {currentCharacter && (
                    <>
                        <CharacterAssignmentPopover
                            token={token}
                            playerId={currentCharacter.playerId}
                            currentCharacterId={currentCharacter.characterId}
                            currentCharacterStatus={currentCharacter.characterStatus}
                            staffPermissions={staffPermissions}
                            onAssignmentChanged={refreshCurrentCharacter}
                        />
                        <button
                            className="print-btn"
                            onClick={() => saveCharacter(currentCharacter)}
                            disabled={saving || autosave}
                        >
                            {saving && !autosave ? 'Saving…' : 'Save Character'}
                        </button>
                        <button
                            className={`print-btn${autosave ? ' print-btn--on' : ''}`}
                            onClick={() => setAutosave(a => {
                                localStorage.setItem('autosave', String(!a));
                                return !a;
                            })}
                        >
                            Autosave: {autosave ? 'ON' : 'OFF'}
                        </button>
                        <button
                            className="print-btn"
                            onClick={() => previewCharacterSheet({ character: currentCharacter })}
                        >
                            👁️ Preview
                        </button>
                        <button
                            className="print-btn"
                            onClick={handlePrintCharacterSheet}
                        >
                            🖨️ Print Character Sheet
                        </button>
                    </>
                )}
            </div>

            {currentCharacter && (
                <>
                    <CharacterForm
                        races={races}
                        skillsets={skillsets}
                        availableSkills={availableSkills}
                        staffPermissions={staffPermissions}
                    />
                    <CharacterSkills
                        token={token}
                        availableSkills={availableSkills}
                    />
                </>
            )}

        </div>
    );
}

export default CharacterEditor;
